import type { Task, Priority } from '../../../../mocks/crm';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const priorityConfig: Record<Priority, { label: string; bg: string; text: string; dot: string; border: string }> = {
  high: { label: 'High', bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500', border: 'border-l-red-400' },
  medium: { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500', border: 'border-l-amber-400' },
  low: { label: 'Low', bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500', border: 'border-l-emerald-400' },
};

const categoryConfig: Record<Task['category'], { icon: string; label: string }> = {
  follow_up: { icon: 'ri-phone-line', label: 'Follow-up' },
  documentation: { icon: 'ri-file-list-3-line', label: 'Documentation' },
  payment: { icon: 'ri-money-dollar-circle-line', label: 'Payment' },
  booking: { icon: 'ri-calendar-check-line', label: 'Booking' },
  other: { icon: 'ri-more-line', label: 'Other' },
};

interface TaskItemProps {
  task: Task;
  onToggle: (id: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
}

export default function TaskItem({ task, onToggle, onEdit, onDelete }: TaskItemProps) {
  const pc = priorityConfig[task.priority];
  const cat = categoryConfig[task.category];
  const isDone = task.status === 'done';
  const isInProgress = task.status === 'in_progress';

  return (
    <div
      className={`group bg-white rounded-2xl border border-gray-100 border-l-4 px-5 py-4 flex items-start gap-4 transition-all hover:border-gray-200 ${pc.border} ${isDone ? 'opacity-60' : ''}`}
    >
      {/* Checkbox */}
      <button
        onClick={() => onToggle(task.id)}
        className="mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all"
        style={
          isDone
            ? { backgroundColor: '#059669', borderColor: '#059669' }
            : { borderColor: '#D1D5DB' }
        }
      >
        {isDone && <i className="ri-check-line text-white text-xs" />}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <p className={`text-sm font-semibold leading-snug flex-1 min-w-0 ${isDone ? 'line-through text-gray-400' : 'text-gray-800'}`}>
            {task.title}
          </p>
          {isInProgress && !isDone && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 flex-shrink-0 whitespace-nowrap">
              In Progress
            </span>
          )}
        </div>

        {task.description && (
          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed line-clamp-1">{task.description}</p>
        )}

        <div className="flex items-center gap-3 mt-2.5 flex-wrap">
          {/* Priority */}
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${pc.bg} ${pc.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
            {pc.label}
          </span>

          {/* Category */}
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <i className={`${cat.icon} text-xs`} />
            {cat.label}
          </span>

          {/* Due Time */}
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <i className="ri-time-line text-xs" />
            {task.dueTime}
          </span>

          {/* Linked Lead */}
          {task.linkedLeadName && (
            <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: brand }}>
              <i className="ri-user-line text-xs" />
              {task.linkedLeadName}
            </span>
          )}

          {/* Assigned To */}
          <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: brand }}
            >
              {task.assignedAvatar}
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:block">{task.assignedTo}</span>
          </div>
        </div>
      </div>

      {/* Actions (visible on hover) */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button
          onClick={() => onEdit(task)}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
        >
          <i className="ri-edit-line text-sm" />
        </button>
        <button
          onClick={() => onDelete(task.id)}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </div>
    </div>
  );
}
